import bowser from "bowser";
import { mobileModel, mobileVendor, osName } from "react-device-detect";
import type {
  BatteryInfo,
  BrowserInfo,
  DeviceInfo,
  OSInfo,
  ScreenInfo,
} from "../types/eonStats";

interface NativeDeviceBridge {
  getDeviceMake?: () => string;
  getDeviceModel?: () => string;
  getDeviceResolution?: () => number;
  getDeviceFirmware?: () => string;
  getDeviceId?: () => string;
  getDeviceRefreshRate?: () => number;
  getDeviceWifiBand?: () => string;
  getDeviceSignal?: () => string;
  getDeviceThermalState?: () => string;
  getDeviceRamPressure?: () => string;
}

interface TizenProductInfo {
  getRealModel?: () => string;
  getFirmware?: () => string;
  getDuid?: () => string;
  is8KPanelSupported?: () => boolean;
  isUdPanelSupported?: () => boolean;
}

interface WebosDeviceInfo {
  modelName?: string;
  screenHeight?: number;
  firmwareVersion?: string;
  version?: string;
}

interface BatteryManagerLike {
  level: number;
  charging: boolean;
}

export interface DeviceInfoPayload<T = string> {
  make: string;
  model: string;
  os: string;
  resolution: number;
  type: T;
}

// ----- Platform detection TV (Samsung, android, LG) -----
function getNativeBridge(): NativeDeviceBridge | undefined {
  const w = window as unknown as {
    HybridBridge?: NativeDeviceBridge;
    _NativeBridge?: NativeDeviceBridge;
  };
  return w.HybridBridge ?? w._NativeBridge;
}
function getTizenProductInfo(): TizenProductInfo | undefined {
  const w = window as unknown as {
    webapis?: { productinfo?: TizenProductInfo };
  };
  return w.webapis?.productinfo;
}
function getWebosDeviceInfoFn():
  | ((cb: (info: WebosDeviceInfo) => void) => void)
  | undefined {
  const w = window as unknown as {
    webOS?: { deviceInfo?: (cb: (info: WebosDeviceInfo) => void) => void };
  };
  return w.webOS?.deviceInfo;
}

const isTizen = (): boolean => getTizenProductInfo() !== undefined;
const isWebos = (): boolean => getWebosDeviceInfoFn() !== undefined;
const isAndroid = (): boolean => getNativeBridge() !== undefined;
// -------------------------

function pruneUndefined(info: DeviceInfo): DeviceInfo {
  (Object.keys(info) as (keyof DeviceInfo)[]).forEach((key) => {
    if (info[key] === undefined) delete info[key];
  });
  return info;
}

// ---- all function to get device information ----
const UNAVAILABLE = "";

// webOS deviceInfo is async (callback); prime + cache at module load
let webosInfoCache: WebosDeviceInfo | null = null;
try {
  getWebosDeviceInfoFn()?.((info) => {
    webosInfoCache = info;
  });
} catch {
  webosInfoCache = null;
}

// Refresh rate: no native API in scope; estimate once via rAF and cache
let refreshRateCache: number | undefined;
function primeRefreshRate(): void {
  if (typeof requestAnimationFrame !== "function") return;
  const samples: number[] = [];
  let last = 0;
  let frames = 0;
  const tick = (t: number) => {
    if (last) samples.push(1000 / (t - last));
    last = t;
    if (++frames < 10) {
      requestAnimationFrame(tick);
    } else if (samples.length) {
      const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
      refreshRateCache = Math.round(avg);
    }
  };
  requestAnimationFrame(tick);
}
primeRefreshRate();

// Audio input presence: async enumerate; prime + cache at module load
let audioInputCache: boolean | undefined;
function primeAudioInput(): void {
  try {
    navigator.mediaDevices
      ?.enumerateDevices?.()
      .then((devices) => {
        audioInputCache = devices.some((d) => d.kind === "audioinput");
      })
      .catch(() => {
        audioInputCache = undefined;
      });
  } catch {
    audioInputCache = undefined;
  }
}
primeAudioInput();

// Tizen Wi-Fi signal strength: async getPropertyValue; prime + cache at load
let tizenSignalCache: string | undefined;
function primeTizenSignal(): void {
  if (!isTizen()) return;
  const systeminfo = (
    window as unknown as {
      tizen?: {
        systeminfo?: {
          getPropertyValue?: (
            property: string,
            onSuccess: (data: { signalStrength?: number }) => void,
            onError?: (err: unknown) => void,
          ) => void;
        };
      };
    }
  ).tizen?.systeminfo;
  try {
    systeminfo?.getPropertyValue?.(
      "WIFI_NETWORK",
      (data) => {
        if (typeof data.signalStrength === "number") {
          tizenSignalCache = String(data.signalStrength);
        }
      },
      () => {
        tizenSignalCache = undefined;
      },
    );
  } catch {
    tizenSignalCache = undefined;
  }
}
primeTizenSignal();

// CPU/thermal pressure (Compute Pressure API — Chromium only): async; prime + cache
let thermalStateCache: string | undefined;
function primeThermalState(): void {
  const PressureObserverCtor = (
    window as unknown as {
      PressureObserver?: new (
        callback: (records: { state?: string }[]) => void,
      ) => { observe: (source: string) => Promise<void> };
    }
  ).PressureObserver;
  if (typeof PressureObserverCtor !== "function") return;
  try {
    const observer = new PressureObserverCtor((records) => {
      const latest = records[records.length - 1];
      if (latest?.state) thermalStateCache = latest.state;
    });
    observer.observe("cpu").catch(() => {
      thermalStateCache = undefined;
    });
  } catch {
    thermalStateCache = undefined;
  }
}
primeThermalState();

// Individual detectors (sync unless noted)
export function getBrowserInfo(): BrowserInfo {
  const parser = bowser.getParser(window.navigator.userAgent);
  return {
    useragent: navigator.userAgent,
    name: parser.getBrowserName(),
    version: parser.getBrowserVersion(),
  };
}

// make and model
export function getMakeAndModel(): { make: string; model: string } {
  const bridge = getNativeBridge();
  if (bridge?.getDeviceMake) {
    return {
      make: bridge.getDeviceMake() || UNAVAILABLE,
      model: bridge.getDeviceModel?.() || UNAVAILABLE,
    };
  }

  if (isTizen()) {
    try {
      return {
        make: "Samsung",
        model: getTizenProductInfo()?.getRealModel?.() || UNAVAILABLE,
      };
    } catch {
      return { make: "Samsung", model: UNAVAILABLE };
    }
  }
  if (isWebos()) {
    return { make: "LG", model: webosInfoCache?.modelName || UNAVAILABLE };
  }

  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua) || /Macintosh|Mac OS X/i.test(ua)) {
    const isIpad =
      /iPad/i.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const model = /iPhone/i.test(ua) ? "iPhone" : isIpad ? "iPad" : "Mac";
    return { make: "Apple", model };
  }

  if (mobileVendor && mobileVendor !== "none") {
    return {
      make: mobileVendor,
      model: mobileModel && mobileModel !== "none" ? mobileModel : UNAVAILABLE,
    };
  }

  return { make: UNAVAILABLE, model: UNAVAILABLE };
}

// Resolution
function getTizenResolution(): number | null {
  try {
    const productinfo = getTizenProductInfo();
    if (productinfo?.is8KPanelSupported?.()) return 4320;
    if (productinfo?.isUdPanelSupported?.()) return 2160;
    return 1080;
  } catch {
    return null;
  }
}
export function getResolution(): number {
  const bridge = getNativeBridge();
  if (bridge?.getDeviceResolution) {
    const res = bridge.getDeviceResolution();
    if (res) return res;
  }
  if (isWebos() && webosInfoCache?.screenHeight) {
    return webosInfoCache.screenHeight;
  }
  if (isTizen()) {
    const tizenHeight = getTizenResolution();
    if (tizenHeight) return tizenHeight;
  }
  return Math.round(window.screen.height * (window.devicePixelRatio || 1));
}

export function getScreenInfo(): ScreenInfo {
  return {
    width: window.screen.width,
    height: window.screen.height,
    dpr: window.devicePixelRatio || 1,
  };
}

export function getFirmware(): string | undefined {
  const bridge = getNativeBridge();
  if (bridge?.getDeviceFirmware) {
    return bridge.getDeviceFirmware() || undefined;
  }
  if (isTizen()) {
    try {
      return getTizenProductInfo()?.getFirmware?.() || undefined;
    } catch {
      return undefined;
    }
  }
  if (isWebos()) {
    return webosInfoCache?.firmwareVersion ?? webosInfoCache?.version;
  }
  return undefined;
}

export function getDeviceId(): string | undefined {
  const bridge = getNativeBridge();
  if (bridge?.getDeviceId) {
    return bridge.getDeviceId() || undefined;
  }
  if (isTizen()) {
    try {
      return getTizenProductInfo()?.getDuid?.() || undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function getCpuCores(): number | undefined {
  return navigator.hardwareConcurrency || undefined;
}

export function getRamGB(): number | undefined {
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  return typeof mem === "number" ? mem : undefined;
}

export async function getBatteryInfo(): Promise<BatteryInfo | undefined> {
  const getBattery = (
    navigator as unknown as {
      getBattery?: () => Promise<BatteryManagerLike>;
    }
  ).getBattery;
  if (typeof getBattery !== "function") return undefined;
  try {
    const battery = await getBattery.call(navigator);
    return { level: battery.level, charging: battery.charging };
  } catch {
    return undefined;
  }
}

export function getTimezone(): string {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = undefined;
  }
  if (tz && /[A-Za-z]/.test(tz)) return tz; // valid IANA name or "UTC"

  const offset = -new Date().getTimezoneOffset(); // minutes east of UTC
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

function getOsInfo(): OSInfo {
  if (isTizen()) return { name: "Tizen", version: "" };
  if (isWebos()) return { name: "webOS", version: "" };
  const parser = bowser.getParser(window.navigator.userAgent);
  const rddOsName = osName && osName !== "none" ? osName : "";
  return {
    name: parser.getOSName() || rddOsName || "",
    version: parser.getOSVersion() || "",
  };
}
function collectCoreInfo(): {
  make: string;
  model: string;
  os: OSInfo;
  resolution: number;
} {
  const { make, model } = getMakeAndModel();
  return { make, model, os: getOsInfo(), resolution: getResolution() };
}
export function buildDeviceInfo<T>(type: T): DeviceInfoPayload<T> {
  const { make, model, os, resolution } = collectCoreInfo();
  return {
    make,
    model,
    os: os.name,
    resolution,
    type,
  };
}

export async function collectDeviceInfo(type?: string): Promise<DeviceInfo> {
  const bridge = getNativeBridge();
  return pruneUndefined({
    // make/model/os/resolution — all platforms (bridge → Tizen/webOS → UA/screen)
    ...collectCoreInfo(),
    // "tv" for Tizen/webOS/Android; otherwise bowser (mobile/tablet/desktop)
    platform:
      isTizen() || isWebos() || isAndroid()
        ? "tv"
        : bowser.getParser(window.navigator.userAgent).getPlatformType(),
    timezone: getTimezone(), // all platforms (Intl API)
    type, // passed in by app (backend device type)
    browser: getBrowserInfo(), // all platforms (parse UA)
    screen: getScreenInfo(), // all platforms (window.screen)
    cpuCores: getCpuCores(), // all platforms (hardwareConcurrency)
    ramGB: getRamGB(), // Chromium only (deviceMemory, coarse); Safari/Firefox → none
    battery: await getBatteryInfo(), // Chromium desktop/Android; TV has no battery, iOS/Safari → none
    firmware: getFirmware(), // Android bridge + Samsung + LG; web/iOS → none
    deviceId: getDeviceId(), // Android bridge + Samsung (DUID); webOS/web/iOS → none
    refreshRate: bridge?.getDeviceRefreshRate?.() || refreshRateCache, // bridge = exact Hz; else rAF estimate (all platforms)
    audioInput: audioInputCache, // all platforms (enumerateDevices, needs HTTPS)
    wifiBand: bridge?.getDeviceWifiBand?.() || undefined, // Android bridge ONLY; Tizen/webOS/iOS/web → no API
    signal: bridge?.getDeviceSignal?.() || tizenSignalCache || undefined, // Android bridge + Samsung (systeminfo); webOS (privileged)/iOS/web → none
    thermalState:
      bridge?.getDeviceThermalState?.() || thermalStateCache || undefined, // Android bridge + web Chromium (Compute Pressure); Tizen/webOS/iOS → none
    ramPressure: bridge?.getDeviceRamPressure?.() || undefined, // Android bridge ONLY; web/Tizen/webOS/iOS → no API
    // vpnProxy: server-side (BE infers from IP) — not client-detectable
  });
}
// -------------------------
